import { useState, useEffect, useCallback } from 'react';
import PageHeader from '@/components/Layout/PageHeader';
import { billingOpsAPI, soCreateAPI, itemsAPI, leadSourcesAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import SearchableSelect from '@/components/SearchableSelect';
import { toast } from 'sonner';
import {
  FloppyDisk, TrendUp, TrendDown, ChartBar, Funnel,
} from '@phosphor-icons/react';

export default function MarginTrackerPage() {
  const [tab, setTab] = useState('report');

  // COGS Master state
  const [cogsEntries, setCogsEntries] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [cogsSaving, setCogsSaving] = useState(false);

  // Report state
  const [report, setReport] = useState([]);
  const [summary, setSummary] = useState({});
  const [reportLoading, setReportLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [filters, setFilters] = useState({
    start_date: '', end_date: '', branch_id: '', lead_source: '',
  });

  useEffect(() => {
    (async () => {
      const [brRes, lsRes, itemsRes] = await Promise.allSettled([
        soCreateAPI.branches(),
        leadSourcesAPI.list(),
        itemsAPI.list({ per_page: 500 }),
      ]);
      if (brRes.status === 'fulfilled') setBranches(brRes.value.data.branches || []);
      if (lsRes.status === 'fulfilled') setLeadSources(lsRes.value.data.lead_sources || []);
      if (itemsRes.status === 'fulfilled') setAllItems(itemsRes.value.data.items || []);
    })();
  }, []);

  // Load COGS master
  useEffect(() => {
    if (tab === 'cogs') {
      (async () => {
        try {
          const { data } = await billingOpsAPI.getCOGSMaster();
          setCogsEntries(data.entries || []);
        } catch {}
      })();
    }
  }, [tab]);

  const loadReport = useCallback(async () => {
    setReportLoading(true);
    try {
      const { data } = await billingOpsAPI.getMarginReport(filters);
      setReport(data.report || []);
      setSummary(data.summary || {});
    } catch (e) { console.error(e); }
    setReportLoading(false);
  }, [filters]);

  useEffect(() => {
    if (tab === 'report') loadReport();
  }, [tab, loadReport]);

  const handleCOGSSave = async () => {
    setCogsSaving(true);
    try {
      await billingOpsAPI.updateCOGSMaster({ entries: cogsEntries });
      toast.success('COGS Master updated!');
    } catch (e) {
      toast.error('Failed to save COGS Master');
    }
    setCogsSaving(false);
  };

  const updateCOGS = (idx, field, value) => {
    setCogsEntries(prev => prev.map((e, i) => i === idx ? { ...e, [field]: value } : e));
  };

  // Add unconfigured items to COGS
  const addMissingItems = () => {
    const existingIds = new Set(cogsEntries.map(e => e.item_id));
    const missing = allItems.filter(it => !existingIds.has(it.id)).map(it => ({
      item_id: it.id, item_name: it.name, cogs_percent: 0, commission_percent: 0, incentive_amount: 0,
    }));
    if (missing.length === 0) { toast.info('All catalog items are already configured'); return; }
    setCogsEntries(prev => [...prev, ...missing]);
    toast.success(`Added ${missing.length} items to COGS Master`);
  };

  const fmt = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="p-6" data-testid="margin-tracker-page">
      <PageHeader title="Margin Tracker" subtitle="COGS master, commissions, and margin report" />

      {/* Tabs */}
      <div className="mt-6 flex gap-2 border-b border-[#E2E8F0]">
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === 'report' ? 'border-[#0F4C81] text-[#0F4C81]' : 'border-transparent text-[#64748B] hover:text-[#0F172A]'}`}
          onClick={() => setTab('report')} data-testid="margin-tab-report">
          <ChartBar size={14} className="inline mr-1" /> Margin Report
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === 'cogs' ? 'border-[#0F4C81] text-[#0F4C81]' : 'border-transparent text-[#64748B] hover:text-[#0F172A]'}`}
          onClick={() => setTab('cogs')} data-testid="margin-tab-cogs">
          COGS Master
        </button>
      </div>

      {/* Margin Report */}
      {tab === 'report' && (
        <div className="mt-4 space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="text-[10px] text-[#64748B] block mb-1">Start Date</label>
              <Input type="date" value={filters.start_date} onChange={e => setFilters(f => ({ ...f, start_date: e.target.value }))} className="w-[150px] h-8 text-xs" data-testid="margin-start-date" />
            </div>
            <div>
              <label className="text-[10px] text-[#64748B] block mb-1">End Date</label>
              <Input type="date" value={filters.end_date} onChange={e => setFilters(f => ({ ...f, end_date: e.target.value }))} className="w-[150px] h-8 text-xs" data-testid="margin-end-date" />
            </div>
            <div>
              <label className="text-[10px] text-[#64748B] block mb-1">Branch</label>
              <SearchableSelect value={filters.branch_id || ''} onChange={v => setFilters(f => ({ ...f, branch_id: v }))}
                options={[{ value: '', label: 'All Branches' }, ...branches.map(b => ({ value: b.id, label: b.name, keywords: b.code || b.clinic_code || "" }))]}
                placeholder="All Branches" testid="margin-branch-filter" buttonClassName="w-[180px] h-8 text-xs" />
            </div>
            <div>
              <label className="text-[10px] text-[#64748B] block mb-1">Lead Source</label>
              <Select value={filters.lead_source || 'all'} onValueChange={v => setFilters(f => ({ ...f, lead_source: v === 'all' ? '' : v }))}>
                <SelectTrigger className="w-[160px] h-8 text-xs" data-testid="margin-lead-filter"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  {leadSources.map(ls => <SelectItem key={ls.id} value={ls.name}>{ls.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button size="sm" onClick={loadReport} className="h-8 bg-[#0F4C81] text-white hover:bg-[#0B3C68]" data-testid="margin-apply-filters">
              <Funnel size={14} className="mr-1" /> Apply
            </Button>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-5 gap-3">
            {[
              { label: 'Revenue', value: summary.total_revenue, color: 'text-[#0F4C81]' },
              { label: 'COGS', value: summary.total_cogs, color: 'text-red-600' },
              { label: 'Commission', value: summary.total_commission, color: 'text-amber-600' },
              { label: 'Incentive', value: summary.total_incentive, color: 'text-blue-600' },
              { label: 'Margin', value: summary.total_margin, color: summary.total_margin >= 0 ? 'text-green-600' : 'text-red-600' },
            ].map(card => (
              <div key={card.label} className="bg-white border border-[#E2E8F0] rounded-lg p-4" data-testid={`margin-card-${card.label.toLowerCase()}`}>
                <p className="text-[10px] text-[#64748B] uppercase tracking-wider">{card.label}</p>
                <p className={`text-lg font-semibold mt-1 ${card.color}`}>{fmt(card.value)}</p>
              </div>
            ))}
          </div>

          {/* Report Table */}
          <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="margin-report-table">
                <thead>
                  <tr className="bg-[#F8FAFC] text-[#64748B] font-medium">
                    <th className="text-left p-2">Location</th>
                    <th className="text-left p-2">Invoice #</th>
                    <th className="text-left p-2">Date</th>
                    <th className="text-left p-2">Lead Source</th>
                    <th className="text-left p-2">Item</th>
                    <th className="text-right p-2">Selling</th>
                    <th className="text-right p-2">COGS%</th>
                    <th className="text-right p-2">COGS Amt</th>
                    <th className="text-right p-2">Comm%</th>
                    <th className="text-right p-2">Comm Amt</th>
                    <th className="text-right p-2">Incentive</th>
                    <th className="text-right p-2">Margin</th>
                  </tr>
                </thead>
                <tbody>
                  {reportLoading ? (
                    <tr><td colSpan={12} className="text-center py-8 text-[#64748B]">Loading...</td></tr>
                  ) : report.length === 0 ? (
                    <tr><td colSpan={12} className="text-center py-8 text-[#64748B]">No data found. Create invoices to see margin analysis.</td></tr>
                  ) : report.map((r, i) => (
                    <tr key={i} className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC]" data-testid={`margin-row-${i}`}>
                      <td className="p-2">{r.location}</td>
                      <td className="p-2 font-medium">{r.invoice_number}</td>
                      <td className="p-2">{r.date}</td>
                      <td className="p-2">
                        {r.lead_source === 'Hear.com' ? (
                          <span className="bg-purple-50 text-purple-700 text-[10px] px-1.5 py-0.5 rounded-full font-medium">{r.lead_source}</span>
                        ) : r.lead_source || '-'}
                      </td>
                      <td className="p-2">{r.item_name}</td>
                      <td className="p-2 text-right">{fmt(r.selling_price)}</td>
                      <td className="p-2 text-right">{r.cogs_percent}%</td>
                      <td className="p-2 text-right text-red-600">{fmt(r.cogs_amount)}</td>
                      <td className="p-2 text-right">{r.commission_percent}%</td>
                      <td className="p-2 text-right text-amber-600">{fmt(r.commission_amount)}</td>
                      <td className="p-2 text-right">{fmt(r.incentive)}</td>
                      <td className="p-2 text-right font-medium">
                        <span className={r.margin >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {r.margin >= 0 ? <TrendUp size={10} className="inline mr-0.5" /> : <TrendDown size={10} className="inline mr-0.5" />}
                          {fmt(r.margin)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {report.length > 0 && (
              <div className="border-t border-[#E2E8F0] p-3 text-xs text-[#64748B]">
                {report.length} rows | Hear.com: 78% bilateral / 80% unilateral COGS
              </div>
            )}
          </div>
        </div>
      )}

      {/* COGS Master */}
      {tab === 'cogs' && (
        <div className="mt-4 space-y-4">
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={addMissingItems} data-testid="cogs-add-missing">
              Add Missing Items from Catalog
            </Button>
            <span className="text-xs text-[#64748B]">{cogsEntries.length} items configured</span>
            <Button size="sm" onClick={handleCOGSSave} disabled={cogsSaving} className="ml-auto bg-[#0F4C81] text-white hover:bg-[#0B3C68]" data-testid="cogs-save-btn">
              <FloppyDisk size={14} className="mr-1" /> {cogsSaving ? 'Saving...' : 'Save COGS Master'}
            </Button>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
            <strong>Note:</strong> Hear.com invoices use fixed COGS% (78% bilateral / 80% unilateral) regardless of the master. Configure COGS%, Commission%, and Incentive amounts for all other lead sources here.
          </div>

          <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
            <table className="w-full text-sm" data-testid="cogs-table">
              <thead>
                <tr className="bg-[#F8FAFC] text-[#64748B] text-xs font-medium">
                  <th className="text-left p-3">Item Name</th>
                  <th className="text-center p-3 w-[120px]">COGS %</th>
                  <th className="text-center p-3 w-[120px]">Commission %</th>
                  <th className="text-center p-3 w-[140px]">Incentive (Rs)</th>
                </tr>
              </thead>
              <tbody>
                {cogsEntries.length === 0 ? (
                  <tr><td colSpan={4} className="text-center py-8 text-[#64748B]">No COGS entries. Click "Add Missing Items" to populate from catalog.</td></tr>
                ) : cogsEntries.map((entry, idx) => (
                  <tr key={entry.item_id || idx} className="border-t border-[#E2E8F0]" data-testid={`cogs-row-${idx}`}>
                    <td className="p-3 text-sm">{entry.item_name}</td>
                    <td className="p-3">
                      <Input type="number" min="0" max="100" value={entry.cogs_percent}
                        onChange={e => updateCOGS(idx, 'cogs_percent', parseFloat(e.target.value) || 0)}
                        className="text-center h-8 text-xs" data-testid={`cogs-pct-${idx}`} />
                    </td>
                    <td className="p-3">
                      <Input type="number" min="0" max="100" value={entry.commission_percent}
                        onChange={e => updateCOGS(idx, 'commission_percent', parseFloat(e.target.value) || 0)}
                        className="text-center h-8 text-xs" data-testid={`cogs-comm-${idx}`} />
                    </td>
                    <td className="p-3">
                      <Input type="number" min="0" value={entry.incentive_amount}
                        onChange={e => updateCOGS(idx, 'incentive_amount', parseFloat(e.target.value) || 0)}
                        className="text-center h-8 text-xs" data-testid={`cogs-incentive-${idx}`} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
